import { addRecord, getRecordById, updateRecord } from "../../../localDB";
import { accountMapping, statusMapping, sessionType, planWorksheetStatusMapping, SessionItemAvailability } from "../../../visualizations/visualizations";

export async function transformSessionData(rawSession, rawSessionDate, cycleID) {
    return rawSession.map(session => {
        let statusValue = null;

        const status = session.status.status; // e.g. "NotStarted", "InProgress", etc.
        const type = session.status.type;     // e.g. "Regular", "Advanced", etc.

        if (status === "NotStarted") {
            statusValue = statusMapping.pending;
        } else if (status === "InProgress") {
            statusValue = statusMapping.inprogress;
        } else if (status === "Completed") {
            if (type === "Regular") {
                statusValue = statusMapping.success;
            } else if (type === "Advanced") {
                statusValue = statusMapping.advanced;
            } else if (type === "Recovery") {
                statusValue = statusMapping.recovered;
            } else if (type === "Incidental" || type === "PastDue") {
                statusValue = statusMapping.success;
            }
        } else if (status === "Cancelled") {
            statusValue = statusMapping.missed; // Optional
        }

        return {
            ID: session.id,
            SessionNumber: session?.session_number ?? null,
            Remarks: session?.remarks ?? null,
            AgentID: session?.agentID ?? null,
            SessionDate: rawSessionDate ?? null,
            SessionTypeID: null,
            CyclePeriodID: Number(cycleID) ?? null,
            InstitutionID: session?.institution?.id ?? null,
            AccountID: session?.account?.id ?? null,
            ActualStart: session?.status?.start ?? null,
            ActualEnd: session?.status?.end ?? null,
            Status: statusValue ?? null,
            Joint: session?.status?.joint,
            Type: sessionType[type] ?? null,
            Activity: {
                Classification: session?.status?.activity.classification ?? null,
                Description: session?.status?.activity.description ?? null,
            },
            Customer: {
                ID: session?.account?.id ?? null,
                Code: session?.account?.code ?? null,
                Name: session?.account?.name ?? null,
                InstitutionID: session?.institution?.id ?? null,
                InstitutionName: session?.institution?.code ?? null,
                Frequency_count: session?.schedule?.frequency?.count ?? null,
                Frequency_code: session?.schedule?.frequency?.code ?? null,
                Class: session?.account?.classification ?? null,
                Time_Start: session?.time?.start ?? null,
                Time_End: session?.time?.end ?? null,
                Time_call: session?.schedule?.bestTimeToVisit ?? null,
                Day_call: (session?.schedule?.bestDayToVisit ?? '')
                    .split(', ')
                    .map(day => {
                        const dayMap = {
                            Monday: 'M',
                            Tuesday: 'T',
                            Wednesday: 'W',
                            Thursday: 'Th',
                            Friday: 'F',
                            Saturday: 'Sa',
                            Sunday: 'Su'
                        };
                        return dayMap[day.trim()] ?? day;
                    })
                    .join(', ') || null,
                AddressDetail: session?.account?.address?.fullAddress ?? null,
                Area: session?.account?.address?.territory?.name ?? null,
                Type: accountMapping?.[session?.account?.type] ?? null
            }
        };
    });
}

export async function transformEventData(rawEvent, rawSessionDate, cycleID) {
    return rawEvent.map(event => {
        let statusValue = null;

        const status = event.status.status; // e.g. "NotStarted", "InProgress", etc.
        const type = event.status.type;     // e.g. "Regular", "Advanced", etc.

        if (status === "NotStarted") {
            statusValue = statusMapping.pending;
        } else if (status === "InProgress") {
            statusValue = statusMapping.inprogress;
        } else if (status === "Completed") {
            if (type === "Regular") {
                statusValue = statusMapping.success;
            } else if (type === "Advanced") {
                statusValue = statusMapping.advanced;
            } else if (type === "Recovery") {
                statusValue = statusMapping.recovered;
            } else if (type === "Incidental" || type === "PastDue") {
                statusValue = statusMapping.success;
            }
        } else if (status === "Cancelled") {
            statusValue = statusMapping.missed; // Optional
        }

        return {
            ID: event.id,
            SessionNumber: event?.session_number ?? null,
            Remarks: event?.remarks ?? null,
            AgentID: event?.agentID ?? null,
            SessionDate: rawSessionDate ?? null,
            SessionTypeID: null,
            CyclePeriodID: Number(cycleID) ?? null,
            InstitutionID: event?.institution?.id ?? null,
            AccountID: event?.account?.id ?? null,
            ActualStart: event?.status?.start ?? null,
            ActualEnd: event?.status?.end ?? null,
            Status: statusValue ?? null,
            Joint: event?.status?.joint,
            Type: sessionType[type] ?? null,
            Activity: {
                Classification: event?.status?.activity.classification ?? null,
                Description: event?.status?.activity.description ?? null,
            }
        };
    });
}

export async function updateLocalSessionJoint(rawSessionDate, joint) {
    const existing = await getRecordById("SessionJointDays", rawSessionDate);
    if (existing) {
        const updated = {
            ...existing,
            joint: joint
        };

        return await updateRecord("SessionJointDays", updated);
    }
}

export async function updateLocal(rawSession, rawSessionDate, cycleID) {

    for (const session of rawSession) {
        const sessionOrig = await getRecordById("session", session.id);


        if (!sessionOrig) continue; // Skip if not found in local DB

        let statusValue = null;
        const status = session.status.status;
        const type = session.status.type;
        const isJoint = session.status.joint;

        // Determine session status value
        if (status === "NotStarted") {
            statusValue = statusMapping.pending;
        } else if (status === "InProgress") {
            statusValue = statusMapping.inprogress;
        } else if (status === "Completed") {
            if (type === "Regular") {
                statusValue = statusMapping.success;
            } else if (type === "Advanced") {
                statusValue = statusMapping.advanced;
            } else if (type === "Recovery") {
                statusValue = statusMapping.recovered;
            } else {
                statusValue = statusMapping.success;
            }
        } else if (status === "Cancelled") {
            statusValue = statusMapping.missed;
        }

        const accountType = session.account?.type;
        const accountID = accountType === "Default"
            ? Number(`${session?.account?.id ?? 0}${session?.id ?? 0}`)
            : session.account?.id;

        // Update only if values exist
        sessionOrig.SessionNumber = session.session_number ?? sessionOrig.SessionNumber;
        sessionOrig.Remarks = session.remarks ?? sessionOrig.Remarks;
        sessionOrig.AgentID = session.agentID ?? sessionOrig.AgentID;
        sessionOrig.SessionDate = rawSessionDate ?? sessionOrig.SessionDate;
        sessionOrig.CyclePeriodID = Number(cycleID) ?? sessionOrig.CyclePeriodID;
        sessionOrig.InstitutionID = session.institution?.id ?? sessionOrig.InstitutionID;
        sessionOrig.AccountID = accountID ?? sessionOrig.AccountID;
        sessionOrig.ActualStart = session.status?.start ?? sessionOrig.ActualStart;
        sessionOrig.ActualEnd = session.status?.end ?? sessionOrig.ActualEnd;
        sessionOrig.Status = statusValue ?? sessionOrig.Status;
        sessionOrig.VersionID = session.status.versionID ?? sessionOrig.VersionID;
        sessionOrig.Joint = session?.status?.joint ?? sessionOrig.Joint;
        sessionOrig.Type = sessionType[type] ?? null;
        sessionOrig.Activity = {
            Classification: session?.status?.activity.classification ?? null,
            Description: session?.status?.activity.description ?? null,
        }

        // Update localDB
        await updateRecord("session", sessionOrig);
    }
}

export async function insertDefaultAccount(rawSession) {
    for (const session of rawSession) {
        const type = session?.account?.type;

        // Proceed only if type is "Default"
        if (type !== "Default") continue;

        // Generate a unique numeric key: AccountID + SessionID
        const uniqueID = Number(`${session?.account?.id ?? 0}${session?.id ?? 0}`);

        // Check if the customer already exists
        const existing = await getRecordById("customer", uniqueID);
        if (existing) continue;

        // Construct account object
        const acc = {
            ID: uniqueID,
            Code: session?.account?.code ?? null,
            Name: session?.account?.name ?? null,
            InstitutionID: session?.institution?.id ?? null,
            InstitutionName: session?.institution?.code ?? null,
            Frequency_count: session?.schedule?.frequency?.count ?? null,
            Frequency_code: session?.schedule?.frequency?.code ?? null,
            Time_Start: session?.time?.start ?? null,
            Time_End: session?.time?.end ?? null,
            Time_call: session?.schedule?.bestTimeToVisit ?? null,
            Day_call: (session?.schedule?.bestDayToVisit ?? '')
                .split(', ')
                .map(day => {
                    const dayMap = {
                        Monday: 'M',
                        Tuesday: 'T',
                        Wednesday: 'W',
                        Thursday: 'Th',
                        Friday: 'F',
                        Saturday: 'Sa',
                        Sunday: 'Su'
                    };
                    return dayMap[day.trim()] ?? day;
                })
                .join(', ') || null,
            AddressDetail: session?.account?.address?.fullAddress ?? null,
            Area: session?.account?.address?.territory?.name ?? null,
            Type: accountMapping.Default
        };

        await addRecord("customer", acc);
    }
}

export async function insertIncidentalNotExist(rawSession, sessDate, cycleID) {
    for (const session of rawSession) {
        if (session?.status?.type !== 'Incidental') continue;

        const existing = await getRecordById("session", session?.id);
        if (existing) continue;

        let statusValue = null;
        const status = session.status?.status;
        const type = session?.status?.type;
        const isJoint = session?.status?.joint;

        // Determine session status value
        if (status === "NotStarted") {
            statusValue = statusMapping.pending;
        } else if (status === "InProgress") {
            statusValue = statusMapping.inprogress;
        } else if (status === "Completed") {
            statusValue = statusMapping.success;
        } else if (status === "Cancelled") {
            statusValue = statusMapping.missed;
        }

        const acc = {
            ID: session?.id,
            SessionNumber: session?.session_number,
            Remarks: session?.remarks ?? null,
            AgentID: session?.agentID,
            SessionDate: sessDate,
            SessionTypeID: null,
            CyclePeriodID: Number(cycleID),
            InstitutionID: session?.institution?.id,
            AccountID: session?.account?.id,
            RecoverySessionID: null,
            JointCallAgentID: null,
            JointCallRemarks: null,
            AdvancedSessionID: null,
            AdvancedCallReasonID: null,
            AdvancedCallRemarks: null,
            MissedCallReasonID: null,
            MissedCallRemarks: null,
            PastDueSessionID: null,
            ActualStart: session?.status?.start,
            ActualEnd: session?.status?.end,
            Duration: null,
            ActualLocationLat: null,
            ActualLocationLong: null,
            RecipientSignatureFileID: null,
            RecipientPhotoFileID: null,
            LastUpdatedDate: "2025-06-26",
            Status: statusValue,
            Joint: isJoint,
            Type: sessionType[type] ?? null,
            VersionID: session?.status?.versionID,
            Activity: {
                Classification: session?.status?.activity.classification ?? null,
                Description: session?.status?.activity.description ?? null,
            }
        };

        await addRecord("session", acc);
    }
}


export async function transformSessionNotesData(rawNotes, sessionID) {
    return rawNotes.map(note => {
        return {
            ID: note?.id,
            SessionID: sessionID,
            ReminderDate: note?.reminderDate,
            ReminderTime: note?.reminderTime,
            ReminderLocation: note?.location,
            Content: note?.content,
            ContentType: note?.typeValue,
            CreatedDate: note?.createdDate
        };
    });
}


export async function updateSessionNotesData(rawNotes) {
    for (const note of rawNotes) {
        const existingNote = await getRecordById("sessionNotes", note.id);

        if (!existingNote) continue;

        existingNote.ReminderDate = note?.reminderDate ?? existingNote.ReminderDate;
        existingNote.ReminderTime = note?.reminderTime ?? existingNote.ReminderTime;
        existingNote.ReminderLocation = note?.location ?? existingNote.ReminderLocation;
        existingNote.Content = note?.content ?? existingNote.Content;
        existingNote.ContentType = note?.typeValue ?? existingNote.ContentType;
        existingNote.CreatedDate = note?.createdDate ?? existingNote.CreatedDate;

        // Update localDB
        await updateRecord("sessionNotes", existingNote);
    }
}

export async function transformSessionSamplesData(rawSamples, sessionId) {
    return rawSamples.map(sample => {
        return {
            ID: sample?.id,
            ItemID: sample?.item?.id,
            UnitID: sample?.unit?.id,
            Minimum: null,
            StockRoomOnHand: null,
            ShelfOnHand: null,
            SessionID: sessionId,
            Remarks: sample.remarks,
            Requested: null,
            Executed: sample?.provided,
            Lot: null,
            ExpiryDate: null
        };
    });

    // const grouped = {};

    // // Group by Item ID
    // for (const sample of rawSamples) {
    //     const itemId = sample.item.id;
    //     if (!grouped[itemId]) {
    //         grouped[itemId] = {
    //             ID: sample.id, // Or use another unique ID logic if needed
    //             ItemID: itemId,
    //             ItemCode: sample.item.code,
    //             ItemLongName: sample.item.longName,
    //             Remarks: sample.remarks || "",
    //             Samples: [],
    //             PromoMaterials: []
    //         };
    //     }

    //     const unit = sample.item.unit;

    //     grouped[itemId].Samples.push({
    //         ID: sample.id,
    //         UnitID: unit.id,
    //         Name: unit.name,
    //         Executed: sample.provided || 0
    //     });

    //     grouped[itemId].PromoMaterials.push({
    //         ID: sample.id,
    //         UnitID: unit.id,
    //         Name: unit.name,
    //         Executed: sample.materials || 0
    //     });
    // }

    // // Convert grouped object to array
    // return Object.values(grouped);
}

export async function transformSyncSession(rawSession) {
    const cpID = localStorage.getItem("c_id");

    return rawSession.map(session => {
        let statusValue = null;
        const status = session.status?.status;
        const type = session?.status?.type;
        const isJoint = session?.status?.joint;

        // Determine session status value
        if (status === "NotStarted") {
            statusValue = statusMapping.pending;
        } else if (status === "InProgress") {
            statusValue = statusMapping.inprogress;
        } else if (status === "Completed") {
            if (type === "Regular") {
                statusValue = statusMapping.success;
            } else if (type === "Recovery") {
                statusValue = statusMapping.recovered;
            } else if (type === "Advanced") {
                statusValue = statusMapping.advanced;
            } else {
                statusValue = statusMapping.success;
            }
        } else if (status === "Cancelled") {
            statusValue = statusMapping.missed;
        }

        return {
            ID: session?.id,
            SessionNumber: session?.session_number,
            AgentID: session?.agentID,
            SessionDate: session?.date,
            SessionTypeID: null,
            CyclePeriodID: session?.cycleID || 0,
            InstitutionID: session?.institution?.id,
            AccountID: session?.account?.id,
            RecoverySessionID: null,
            JointCallAgentID: null,
            JointCallRemarks: null,
            AdvancedSessionID: null,
            AdvancedCallReasonID: null,
            AdvancedCallRemarks: null,
            MissedCallReasonID: null,
            MissedCallRemarks: null,
            PastDueSessionID: null,
            ActualStart: session?.status?.start,
            ActualEnd: session?.status?.end,
            Duration: null,
            ActualLocationLat: null,
            ActualLocationLong: null,
            RecipientSignatureFileID: null,
            RecipientPhotoFileID: null,
            LastUpdatedDate: "2025-06-26",
            Status: statusValue,
            Joint: session?.status?.joint,
            Type: sessionType[type] ?? null,
            VersionID: session?.status?.versionID,
            Activity: {
                Classification: session?.status?.activity.classification ?? null,
                Description: session?.status?.activity.description ?? null,
            }
        }
    });
}

export async function transformSessionInventorySamples(rawInventory, sessionId) {
    return rawInventory.flatMap(inventory =>
        (inventory.details || []).map(details => ({
            ID: details?.id,
            ItemID: inventory?.item?.id,
            UnitID: inventory?.unit?.id,
            Minimum: 0,
            StockRoomOnHand: 0,
            ShelfOnHand: 0,
            SessionID: sessionId,
            Remarks: details.remarks,
            Requested: inventory?.requested,
            Executed: inventory?.executed,
            Lot: details?.lotNumber,
            ExpiryDate: new Date(details.expiry).toLocaleDateString('en-CA'),
            LocationID: details?.location
        }))
    );
}

export async function transformSessionInventory(rawInventory, sessionId) {
    return rawInventory.flatMap(inventory => {
        const grouped = {};

        (inventory.details || []).forEach(detail => {
            const key = `${detail.lotNumber}_${detail.expiry}}`;
            if (!grouped[key]) {
                grouped[key] = {
                    ID: detail.id || 0,
                    Lot: detail.lotNumber,
                    ExpiryDate: new Date(detail.expiry).toLocaleDateString('en-CA'),
                    StockRoomOnHand: 0,
                    ShelfOnHand: 0,
                    Remarks: null,
                    Availability: SessionItemAvailability.Carried
                };
            }

            if (detail.location === 1) {
                grouped[key].StockRoomOnHand = detail.executed || 0;
                grouped[key].Remarks = detail.remarks || null;
                grouped[key].Availability = SessionItemAvailability[detail.availability] || SessionItemAvailability.Carried;
            } else if (detail.location === 2) {
                grouped[key].ShelfOnHand = detail.executed || 0;
            }
        });

        return Object.values(grouped).map(group => ({
            ID: Number(`${inventory?.id}${group.ID}`),
            ItemID: inventory?.item?.id,
            UnitID: inventory?.unit?.id,
            Minimum: 0,
            StockRoomOnHand: group.StockRoomOnHand,
            ShelfOnHand: group.ShelfOnHand,
            SessionID: sessionId,
            Remarks: group.Remarks,
            Requested: inventory?.requested,
            Executed: inventory?.executed,
            Lot: group.Lot,
            ExpiryDate: group.ExpiryDate,
            Availability: group.Availability,
        }));
    });
}